import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Navbar from '../components/Navbar';
import AdminTabs from '../components/AdminTabs';
import AdminLoadingScreen from '../components/AdminLoadingScreen';
import { Save, AlertTriangle, Percent, User, Plus, X, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Banner({ type, message, onClose }) {
  const { t } = useTranslation();
  if (!message) return null;
  const isSuccess = type === 'success';
  return (
    <div
      className={`flex items-center justify-between gap-3 px-5 py-4 rounded-2xl mb-6 text-sm font-medium ${
        isSuccess
          ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
          : 'bg-red-50 text-red-800 border border-red-200'
      }`}
    >
      <span>{message}</span>
      <button onClick={onClose} aria-label={t('common.close')} className="shrink-0 hover:opacity-70 transition-opacity">
        <X size={16} />
      </button>
    </div>
  );
}

function ConfirmModal({ fromRate, toRate, onConfirm, onCancel }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full mx-4">
        <h3 className="text-xl font-bold text-[#134168] mb-3">{t('admin.confirmChange')}</h3>
        <p className="text-gray-600 mb-6">
          <Trans i18nKey="admin.confirmChangeDesc" values={{ from: (fromRate * 100).toFixed(0), to: (toRate * 100).toFixed(0) }} />
        </p>
        <div className="flex gap-3">
          <button
            onClick={onConfirm}
            className="flex-1 bg-[#134168] text-white py-3 rounded-full font-semibold hover:bg-[#39b4aa] transition-colors"
          >
            {t('admin.confirm')}
          </button>
          <button
            onClick={onCancel}
            className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-full font-semibold hover:bg-gray-50 transition-colors"
          >
            {t('admin.cancelBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CommissionConfig() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [originalRate, setOriginalRate] = useState(0.15);
  const [draftRate, setDraftRate] = useState(0.15);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [banner, setBanner] = useState({ type: '', message: '' });

  const [overrides, setOverrides] = useState([]);
  const [overrideForm, setOverrideForm] = useState({
    provider_email: '',
    rate: '',
    expires_at: '',
  });
  const [addingOverride, setAddingOverride] = useState(false);
  const [overrideBanner, setOverrideBanner] = useState({ type: '', message: '' });

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/commission-config`, {
        credentials: 'include',
      });
      if (res.status === 401 || res.status === 403) {
        navigate('/login');
        return;
      }
      if (!res.ok) throw new Error('Error');
      const data = await res.json();
      const rate = data.global_rate ?? 0.15;
      setOriginalRate(rate);
      setDraftRate(rate);
      setOverrides(
        data.provider_overrides
          ? Object.entries(data.provider_overrides).map(([id, val]) => ({
              provider_id: id,
              ...val,
            }))
          : []
      );
    } catch {
      setBanner({ type: 'error', message: t('admin.configLoadError') });
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSaveClick = () => {
    setBanner({ type: '', message: '' });
    setShowModal(true);
  };

  const handleConfirmSave = async () => {
    setShowModal(false);
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/commission-config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ global_rate: draftRate }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error');
      }
      setOriginalRate(draftRate);
      setBanner({ type: 'success', message: t('admin.commissionUpdated') });
    } catch (e) {
      setBanner({ type: 'error', message: t('admin.savingError', { m: e.message }) });
    } finally {
      setSaving(false);
    }
  };

  const handleRateChange = (e) => {
    const val = parseFloat(e.target.value);
    if (isNaN(val)) return;
    setDraftRate(Math.min(0.3, Math.max(0, val)));
  };

  const handleDeleteOverride = async (overrideId) => {
    if (!overrideId) return;
    if (!window.confirm(t('admin.overrideDeleteConfirm', '¿Eliminar este override de comisión?'))) return;
    setOverrideBanner({ type: '', message: '' });
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/commission-config/provider-override/${overrideId}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || t('admin.overrideDeleteError', 'No se pudo eliminar'));
      }
      setOverrideBanner({ type: 'success', message: t('admin.overrideDeletedOk', 'Override eliminado') });
      await fetchConfig();
    } catch (e) {
      setOverrideBanner({ type: 'error', message: e.message });
    }
  };

  const handleAddOverride = async () => {
    const { provider_email, rate, expires_at } = overrideForm;
    if (!provider_email || rate === '') {
      setOverrideBanner({ type: 'error', message: t('admin.overrideEmailRateRequired') });
      return;
    }
    // Validación cliente-side del rango 0–0.30 (el backend también valida).
    // Antes se podía enviar rate=0.99 y el servidor lo rechazaba, pero el
    // admin veía un error genérico. Mejor mostrar el rango claro aquí.
    const rateNum = parseFloat(rate);
    if (Number.isNaN(rateNum) || rateNum < 0 || rateNum > 0.30) {
      setOverrideBanner({ type: 'error', message: t('admin.overrideRateRange', 'La tasa debe estar entre 0 y 0.30 (0% - 30%).') });
      return;
    }
    setAddingOverride(true);
    setOverrideBanner({ type: '', message: '' });
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/commission-config/provider-override`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            provider_email,
            rate: parseFloat(rate),
            expires_at: expires_at || null,
          }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error');
      }
      setOverrideBanner({ type: 'success', message: t('admin.overrideSavedOk') });
      setOverrideForm({ provider_email: '', rate: '', expires_at: '' });
      await fetchConfig();
    } catch (e) {
      setOverrideBanner({ type: 'error', message: `❌ ${e.message}` });
    } finally {
      setAddingOverride(false);
    }
  };

  if (loading) {
    return <AdminLoadingScreen message={t('admin.loadingCommission', 'Cargando configuración de comisiones...')} />;
  }

  const isDirty = draftRate !== originalRate;
  const isZero = draftRate === 0;
  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-ES';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <AdminTabs />

      {showModal && (
        <ConfirmModal
          fromRate={originalRate}
          toRate={draftRate}
          onConfirm={handleConfirmSave}
          onCancel={() => setShowModal(false)}
        />
      )}

      <div className="container mx-auto px-4 md:px-6 max-w-3xl py-10">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="mb-8 flex items-center gap-2 text-gray-500 hover:text-[#134168] font-medium transition-colors"
        >
          {t('admin.backToPanelShort')}
        </button>

        <h1 className="text-4xl font-heading font-bold mb-8 text-[#134168]">
          {t('admin.commissionConfigTitle')}
        </h1>

        <Banner
          type={banner.type}
          message={banner.message}
          onClose={() => setBanner({ type: '', message: '' })}
        />

        <div className="bg-white rounded-3xl p-8 shadow-sm mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#134168]/10 flex items-center justify-center">
              <Percent size={20} className="text-[#134168]" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">{t('admin.globalCommission')}</h2>
          </div>

          <div className="mb-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t('admin.globalRate')}
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0"
                max="0.30"
                value={draftRate}
                onChange={handleRateChange}
                className="w-full px-4 py-3 pr-14 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168] text-lg"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium text-sm">
                {(draftRate * 100).toFixed(0)}%
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-1.5">{t('admin.minMaxNote')}</p>
          </div>

          {isZero && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-xl px-4 py-3 mb-5">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>{t('admin.zeroWarn')}</span>
            </div>
          )}

          <button
            onClick={handleSaveClick}
            disabled={saving || !isDirty}
            className="w-full flex items-center justify-center gap-2 bg-[#134168] text-white py-4 rounded-full font-bold text-base hover:bg-[#39b4aa] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('admin.saveLoading')}
              </>
            ) : (
              <>
                <Save size={18} />
                {t('providerAssets.saveChanges')}
              </>
            )}
          </button>
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#39b4aa]/15 flex items-center justify-center">
              <User size={20} className="text-[#39b4aa]" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">{t('admin.commissionPerProvider')}</h2>
          </div>

          <Banner
            type={overrideBanner.type}
            message={overrideBanner.message}
            onClose={() => setOverrideBanner({ type: '', message: '' })}
          />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="sm:col-span-1">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                {t('admin.providerEmail')}
              </label>
              <input
                type="email"
                placeholder={t('admin.providerEmailPlaceholder')}
                value={overrideForm.provider_email}
                onChange={(e) =>
                  setOverrideForm({ ...overrideForm, provider_email: e.target.value })
                }
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#39b4aa]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                {t('admin.rateLabel')}
              </label>
              <input
                type="number"
                placeholder="0.10"
                step="0.01"
                min="0"
                max="0.30"
                value={overrideForm.rate}
                onChange={(e) =>
                  setOverrideForm({ ...overrideForm, rate: e.target.value })
                }
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#39b4aa]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                {t('admin.expiresOnOptional')}
              </label>
              <input
                type="date"
                value={overrideForm.expires_at}
                onChange={(e) =>
                  setOverrideForm({ ...overrideForm, expires_at: e.target.value })
                }
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#39b4aa]"
              />
            </div>
          </div>

          <button
            onClick={handleAddOverride}
            disabled={addingOverride}
            className="flex items-center gap-2 bg-[#39b4aa] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#134168] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-8"
          >
            {addingOverride ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Plus size={16} />
            )}
            {t('admin.addOverride')}
          </button>

          {overrides.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">
              {t('admin.noOverrides')}
            </p>
          ) : (
            <div className="space-y-3">
              {overrides.map((ov, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-2xl px-4 py-3"
                >
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      {ov.provider_email || ov.provider_id}
                      {ov.provider_name && (
                        <span className="text-xs font-normal text-gray-500 ml-2">({ov.provider_name})</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t('admin.rateLine')} <strong>{(ov.rate * 100).toFixed(0)}%</strong>
                      {ov.expires_at && (
                        <>
                          {' '}· {t('admin.expiresLine')}{' '}
                          <strong>
                            {new Date(ov.expires_at).toLocaleDateString(locale)}
                          </strong>
                        </>
                      )}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteOverride(ov.provider_id)}
                    aria-label={t('admin.overrideDelete', 'Eliminar override')}
                    className="p-1 rounded hover:bg-red-50 transition-colors"
                  >
                    <Trash2 size={16} className="text-gray-300 hover:text-red-500 transition-colors" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
